import React, { useEffect } from 'react';
import { Collapse, IconButton, MenuItem, Slide, Typography } from '@mui/material';
import Grid from '@mui/material/Unstable_Grid2';
import moment from 'moment';
import { fetchFinancialControlTotals } from 'services/reporting';
import ErrorDialog from 'components/ErrorDialog';
import { ERRORS } from 'constants/index';
import { FinancialControlTotalAccount, ReportingTabProps } from 'types/reporting.type';
import { Page } from 'components/Layout';
import LrGrid from 'components/LrGrid';
import Spacing from 'components/Layout/components/Spacing';
import { columnDefs } from './GridDefs';
import { Select } from 'components/Input';

import KeyboardArrowLeftIcon from '@mui/icons-material/KeyboardArrowLeft';
import KeyboardArrowRightIcon from '@mui/icons-material/KeyboardArrowRight';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import { DatePicker } from '@mui/x-date-pickers';

const FinancialControl: React.FunctionComponent<ReportingTabProps> = (props) => {
    const { reportName } = props;
    const [startDate, setStartDate] = React.useState(moment().subtract(1, 'days').startOf('day'));
    const [endDate, setEndDate] = React.useState(moment().subtract(1, 'days').endOf('day'));
    const [showCustomTime, setShowCustomTime] = React.useState(false);
    const [showDataDetails, setShowDataDetails] = React.useState(false);
    const [selectedWindow, setSelectedWindow] = React.useState(1);
    const [controlPayableData, setControlPayableData] = React.useState([] as FinancialControlTotalAccount[]);
    const [controlReceivableData, setControlReceivableData] = React.useState([] as FinancialControlTotalAccount[]);
    const [controlCombinedData, setControlCombinedData] = React.useState([] as FinancialControlTotalAccount[]);
    const [error, setError] = React.useState(false);

    const fetchControlTotals = async () => {
        try {
            const response = await fetchFinancialControlTotals({ startDate: startDate, endDate: endDate });
            const payableData = Object.values(response.accountsPayable.accountControlTotals);
            const receivableData = Object.values(response.accountsReceivable.accountControlTotals);
            const combinedData = Object.values(response.combinedAccounts.accountControlTotals);
            setControlCombinedData(combinedData);
            setControlPayableData(payableData);
            setControlReceivableData(receivableData);
        } catch (e) {
            setError(true);
        }
    };

    const renderDataGrid = (rowData: FinancialControlTotalAccount[], height: string) => (
        <LrGrid
            gridClassName="ag-theme-balham"
            columnDefs={columnDefs}
            rowData={rowData}
            hideStatusBar
            sizeToFit
            height={height}
        />
    );

    useEffect(() => {
        fetchControlTotals();
    }, [startDate, endDate]);

    const handleTimeWindowSelectChange = (event: any) => {
        const newTimeWindow = event.target.value;
        setSelectedWindow(newTimeWindow);
        if (newTimeWindow < 0) {
            setShowCustomTime(true);
        } else {
            setShowCustomTime(false);
            setStartDate(moment().subtract(newTimeWindow, 'days').startOf('day'));
            setEndDate(moment().subtract(1, 'days').endOf('day'));
        }
    };

    return (
        <>
            <Grid xs={10}>
                <Page title={'Financial Control Totals'}>
                    {/* LEGACY: Do not use this pattern. Use the error context (useErrorDialog) instead. */}
                    {/* See examples in `src/views/SubmissionDetails/index.tsx` and `src/views/SubmissionDetails/components/QuotesBind/index.tsx` */}
                    {error && (
                        <ErrorDialog errorTitle={ERRORS.API_ERROR_MSG_TITLE} errorMsg={ERRORS.API_ERROR_MSG_BODY} />
                    )}
                    <Grid container spacing={1} style={{ height: '80px' }}>
                        <Grid xs={3} style={{ display: 'flex', alignItems: 'center' }}>
                            <Select
                                variant={'standard'}
                                id={'time-window-select'}
                                label={'Time Window'}
                                onChange={handleTimeWindowSelectChange}
                                value={selectedWindow}
                            >
                                <MenuItem value={1}>Yesterday</MenuItem>
                                <MenuItem value={7}>Last 7 Days</MenuItem>
                                <MenuItem value={30}>Last 30 Days</MenuItem>
                                <MenuItem value={-1}>Custom</MenuItem>
                            </Select>
                            <div>
                                <IconButton
                                    aria-label="expand custom date picker"
                                    size="small"
                                    onClick={() => {
                                        setShowCustomTime(!showCustomTime);
                                        setSelectedWindow(-1);
                                    }}
                                >
                                    {showCustomTime ? <KeyboardArrowLeftIcon /> : <KeyboardArrowRightIcon />}
                                </IconButton>
                            </div>
                        </Grid>
                        <Grid xs={4}>
                            <Slide direction={'right'} in={showCustomTime} mountOnEnter unmountOnExit>
                                <div style={{ display: 'flex' }}>
                                    <DatePicker
                                        label="Start Date"
                                        disableFuture
                                        format="MM/DD/YYYY"
                                        value={startDate}
                                        onChange={(date) => date && setStartDate(date)}
                                    />

                                    <DatePicker
                                        label="End Date"
                                        disableFuture
                                        format="MM/DD/YYYY"
                                        value={endDate}
                                        onChange={(date) => date && setEndDate(date)}
                                    />
                                </div>
                            </Slide>
                        </Grid>
                    </Grid>
                    <Spacing height={'18px'} />
                    <Grid container direction={'column'} spacing={2} alignItems={'flex-start'}>
                        <Grid>
                            <Typography variant="h4">Control Accounts</Typography>
                        </Grid>
                        <Spacing height="12px" />
                        <Grid style={{ width: '350px' }}>
                            {renderDataGrid(controlCombinedData, '350px')}
                            <Spacing height="5px" />
                            <div
                                style={{ display: 'flex', alignItems: 'center' }}
                                onClick={() => {
                                    setShowDataDetails(!showDataDetails);
                                }}
                            >
                                <Typography>Details</Typography>
                                <IconButton style={{ marginBottom: '8px' }} aria-label="expand details" size="small">
                                    {showDataDetails ? <KeyboardArrowUpIcon /> : <KeyboardArrowDownIcon />}
                                </IconButton>
                            </div>
                        </Grid>
                    </Grid>
                    <Collapse in={showDataDetails} mountOnEnter unmountOnExit>
                        <Grid container spacing={2} alignItems="center">
                            <Grid xs={6}>
                                <Typography variant="h4">Accounts Payable</Typography>
                            </Grid>
                            <Grid xs={6}>
                                <Typography variant="h4">Accounts Recievable</Typography>
                            </Grid>
                            <Spacing height="12px" />
                            <Grid xs={6}>{renderDataGrid(controlPayableData, '300px')}</Grid>
                            <Grid xs={6}>{renderDataGrid(controlReceivableData, '300px')}</Grid>
                        </Grid>
                    </Collapse>
                </Page>
            </Grid>
        </>
    );
};

export default FinancialControl;
